import { FC, HTMLAttributes, useState } from "react";
import { useStyles } from "./FriendCard.styles";
import { Typography } from "../../core/Typography/Typography";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import Avatar from "../../core/Avatar/Avatar";
import { Icon } from "../../core/Icon/Icon";
import Button from "../../core/Button/Button";
import { getNewFollowStatus } from "../../../models/friend";
import { useTranslation } from "react-i18next";
import { Time } from "../../../models/time";
import { useNavigate } from "react-router-dom";
import {
  FollowStatus,
  FollowSuggestionCriteria,
  TierLevel,
  User as UserType,
} from "@onmo/graphql/graphql";
import { User } from "../../../models/user";
import { useFollowUser } from "../../../hooks/resolvers/friend/friend";
import { useWalletConfig } from "../../../hooks/useWalletConfig";

interface IFriendCardProps {
  user: UserType;
  tierLevels?: TierLevel[];
  criteria?: FollowSuggestionCriteria;
}

export const FriendCard: FC<
  IFriendCardProps & HTMLAttributes<HTMLDivElement>
> = ({ user, tierLevels, criteria }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { walletConfig } = useWalletConfig();
  const baseCoinType = walletConfig.primaryCurrency.base;
  const userRank = User.getCurrentRank({
    xp: user.xp,
    wallet: user.coinWallets,
    tierLevels,
    baseCoinType,
  });
  const [currentFriendStatus, setCurrentFriendStatus] = useState<FollowStatus>(
    user.followStatus,
  );
  const followUser = useFollowUser(user);

  const onFollowUser = async () => {
    try {
      followUser();
      setCurrentFriendStatus(getNewFollowStatus(user.followStatus));
    } catch (error) {
      console.error(error);
    }
  };

  const renderFollowAction = () => {
    switch (currentFriendStatus) {
      case FollowStatus.FollowedBy:
        return (
          <Icon
            name="icon-profile-tick"
            className={classes.icon}
            data-testid="mutual-friends"
          />
        );
      case FollowStatus.FollowingAndFollowedBy:
        return (
          <Icon
            name="icon-profile-2user"
            className={classes.icon}
            data-testid="friends"
          />
        );

      default:
        return (
          <Button
            afterIcon="icon-linear-add"
            size="small"
            onClick={onFollowUser}
            data-testid="add-friend-button"
          />
        );
    }
  };

  return (
    <div className={classes.root}>
      <Avatar
        size="m"
        url={user?.avatar}
        userRank={userRank}
        onClick={() => navigate(`/profile/${user.id}`)}
        status={Time.getOnlineStatus(user?.lastActive?.time)}
        hideProgress
      />
      <div className={classes.content}>
        <div className={classes.name}>
          <Typography variant={TypographyVariant.BodyB5Medium} lines={1}>
            {user?.username}
          </Typography>
          &nbsp;
          {currentFriendStatus === FollowStatus.FollowingAndFollowedBy && (
            <Icon name="icon-profile-tick" size={16} />
          )}
        </div>
        {criteria && (
          <div className={classes.name}>
            <Icon name="icon-like" size={20} className={classes.likeIcon} />
            &nbsp;
            <Typography
              variant={TypographyVariant.BodyB5Regular}
              color={TypographyColor.Silver}
              lines={1}
            >
              {t(criteria)}
            </Typography>
          </div>
        )}
      </div>
      {renderFollowAction()}
    </div>
  );
};
